<template>
  <div class="search-results-carousel">
    <!-- Neural Animation Row - Always visible when searching -->
    <div v-if="!finalAnswerReceived" class="neural-row">
      <div class="status-wrapper">
        <div class="status-icon">
          <div class="neural-animation">
            <div class="neural-pulse"></div>
            <div class="neural-ring"></div>
          </div>
        </div>
        <div class="status-content">
          <div class="status-progress">
            <div class="waveform">
              <div v-for="n in 24" :key="n"
                   class="waveform-bar"
                   :class="{ 'active': isBarActive(n) }">
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="results-counter">
        <span class="counter-number">{{ processedResults.length }}</span>
        <span class="counter-separator">/</span>
        <span class="counter-total">{{ results.length }}</span>
      </div>
    </div>

    <!-- Header with toggle -->
    <div v-if="finalAnswerReceived" class="carousel-header">
      <div class="carousel-title-area">
        <h3 class="carousel-title">Search Results</h3>
        <span class="results-count" v-if="processedResults.length > 0">({{ processedResults.length }})</span>
      </div>
      <div class="carousel-controls">
        <button
          class="toggle-btn"
          @click="isExpanded = !isExpanded"
          title="Toggle search results view"
        >
          {{ isExpanded ? '▼' : '▶' }}
        </button>
      </div>
    </div>

    <!-- Collapsed View -->
    <div v-if="finalAnswerReceived && !isExpanded && processedResults.length > 0" class="collapsed-view" @click="isExpanded = true">
      <div class="collapsed-preview">
        <div class="collapsed-thumbnails">
          <div
            v-for="result in processedResults.slice(0, 4)"
            :key="result.isPlaceholder ? result.id : result.url"
            class="collapsed-thumbnail"
            :title="result.title"
          >
            <!-- Image thumbnails -->
            <img v-if="result.thumbnail || result.image"
                 :src="result.thumbnail || result.image"
                 :alt="result.title"
                 @error="handleImageError">
            <!-- Placeholder -->
            <div v-else class="thumbnail-placeholder-mini"></div>
          </div>
          <div v-if="processedResults.length > 4" class="more-indicator">
            +{{ processedResults.length - 4 }}
          </div>
        </div>
        <div class="collapsed-info">
          <span class="collapsed-text">{{ processedResults.length }} search results</span>
        </div>
      </div>
    </div>

    <!-- Simplified Horizontal Carousel (Only shown when expanded or searching) -->
    <div class="carousel-container" v-if="!finalAnswerReceived || isExpanded">
      <div class="scroll-arrow left" @click.stop="scrollCarousel('left')" v-if="showScrollButtons && hasScrollLeft">
        <span>◀</span>
      </div>

      <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
        <a
          v-for="(result) in processedResults"
          :key="result.isPlaceholder ? result.id : result.url"
          :href="result.url"
          target="_blank"
          rel="noopener noreferrer"
          class="carousel-item"
          :class="{
            'active': isResultActive(result),
            'placeholder': result.isPlaceholder
          }"
        >
          <div class="result-card">
            <div class="result-image">
              <img v-if="result.thumbnail || result.image"
                  :src="result.thumbnail || result.image"
                  :alt="result.title"
                  @error="handleImageError">
              <div v-else class="thumbnail-placeholder"></div>
            </div>
            <div class="result-info">
              <div class="result-title">{{ truncateTitle(result.title) }}</div>
              <div class="result-source">{{ getDomainFromUrl(result.url) }}</div>
            </div>
          </div>
        </a>
      </div>

      <div class="scroll-arrow right" @click.stop="scrollCarousel('right')" v-if="showScrollButtons && hasScrollRight">
        <span>▶</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchResultsCarousel',
  props: {
    results: {
      type: Array,
      required: true
    },
    isSearching: {
      type: Boolean,
      default: false
    },
    finalAnswerReceived: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: false, // Start collapsed when final answer received
      activeSpeakingUrls: null,
      processedResults: [],
      statusMessages: [
        'Initializing neural pathways...',
        'Analyzing semantic patterns...',
        'Synthesizing knowledge graph...'
      ],
      currentStatusIndex: 0,
      showScrollButtons: true,
      hasScrollLeft: false,
      hasScrollRight: true,
      carouselScrollPosition: 0,
      autoScrollInterval: null
    }
  },
  watch: {
    results: {
      immediate: true,
      handler(newResults) {
        if (newResults.length === 0) return;
        this.processedResults = [];
        if (newResults.length > 0) {
          this.animateResults(newResults);
          // Start auto-scrolling when results are loaded
          this.$nextTick(() => {
            this.startAutoScroll();
          });
        }
      }
    },
    finalAnswerReceived: {
      handler(newVal) {
        if (newVal) {
          // Collapse the view when final answer is received
          this.isExpanded = false;
          this.$emit('update:finalAnswer', true);
        }
      }
    }
  },
  methods: {
    isResultActive(result) {
      if (!this.activeSpeakingUrls || !result.url) return false;

      // Direct match
      if (this.activeSpeakingUrls[result.url]) return true;

      // Try to match with URL variations
      const urls = Object.keys(this.activeSpeakingUrls);
      return urls.some(url =>
        url.includes(result.url) || result.url.includes(url)
      );
    },
    isBarActive(index) {
      // Create a wave-like pattern
      const time = Date.now() / 200;
      const amplitude = Math.sin((time + index) / 2) * 0.5 + 0.5;
      return amplitude > 0.5;
    },
    // Improved setActiveSpeakingUrls method:
    setActiveSpeakingUrls(urls) {
      // Initialize empty object
      const urlsObject = {};

      // Handle different formats of input
      if (urls) {
        // Case 1: Array of arrays (from citation map entries)
        if (Array.isArray(urls) && urls.length > 0 && Array.isArray(urls[0])) {
          urls.forEach(entry => {
            // Each entry is [id, url]
            if (Array.isArray(entry) && entry.length >= 2) {
              const url = entry[1];
              if (url && typeof url === 'string') {
                urlsObject[url] = true;
              }
            }
          });
        }
        // Case 2: Array of URLs
        else if (Array.isArray(urls)) {
          urls.forEach(url => {
            if (url && typeof url === 'string') {
              urlsObject[url] = true;
            }
          });
        }
        // Case 3: Single URL string
        else if (typeof urls === 'string') {
          urlsObject[urls] = true;
        }
        // Case 4: Already an object
        else if (typeof urls === 'object') {
          Object.assign(urlsObject, urls);
        }
      }

      this.activeSpeakingUrls = urlsObject;

      // Auto-expand the carousel when a citation is active
      if (Object.keys(urlsObject).length > 0) {
        this.isExpanded = true;
      }

      // Scroll to active items after a slight delay to ensure rendering
      setTimeout(() => {
        this.scrollToActiveItem();
      }, 100);
    },

    // Improved scrollToActiveItem method:
    scrollToActiveItem() {
      if (!this.$refs.carouselTrack || !this.activeSpeakingUrls) {
        return;
      }

      // Get active URLs
      const activeUrls = Object.keys(this.activeSpeakingUrls);
      if (!activeUrls.length) {
        return;
      }

      // Find the carousel item for the URL
      let activeItem = null;
      const items = this.$refs.carouselTrack.querySelectorAll('.carousel-item');

      for (const item of items) {
        const itemHref = item.getAttribute('href');

        // Try both exact and partial matching
        if (activeUrls.some(url => {
          return itemHref === url ||
                (itemHref && url && itemHref.includes(url)) ||
                (url && itemHref && url.includes(itemHref));
        })) {
          activeItem = item;
          break;
        }
      }

      if (activeItem) {
        // Calculate position for centering the item
        const trackWidth = this.$refs.carouselTrack.clientWidth;
        const itemLeft = activeItem.offsetLeft;
        const itemWidth = activeItem.offsetWidth;

        // Calculate the scroll position that centers the item
        const scrollPosition = itemLeft - (trackWidth / 2) + (itemWidth / 2);

        // Smooth scroll to the calculated position
        this.$refs.carouselTrack.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });

        // Add active class to highlight the item
        this.$nextTick(() => {
          items.forEach(item => item.classList.remove('active-scroll'));
          activeItem.classList.add('active-scroll');
        });
      }
    },

    getDomainFromUrl(url) {
      try {
        const urlObject = new URL(url);
        return urlObject.hostname.replace('www.', '');
      } catch (e) {
        // If URL parsing fails, return the original URL
        return url;
      }
    },
    truncateTitle(title) {
      return title ? (title.length > 25 ? title.substring(0, 25) + '...' : title) : 'No Title';
    },
    handleImageError(e) {
      e.target.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'thumbnail-placeholder';
      e.target.parentNode.appendChild(placeholder);
    },
    animateResults(results) {
      results.forEach((result, index) => {
        setTimeout(() => {
          this.processedResults.push(result);
          this.currentStatusIndex = (index % this.statusMessages.length);
        }, index * 10);
      });
    },

    // Improved scrollCarousel with smooth behavior
    scrollCarousel(direction) {
      if (!this.$refs.carouselTrack) return;

      const scrollAmount = this.$refs.carouselTrack.clientWidth / 2; // Half of visible width
      const currentScroll = this.$refs.carouselTrack.scrollLeft;

      if (direction === 'left') {
        this.$refs.carouselTrack.scrollTo({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        this.$refs.carouselTrack.scrollTo({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }

      // Update scroll state after animation
      this.$nextTick(() => {
        this.handleScroll();
      });
    },

    // Start auto-scrolling feature
    startAutoScroll() {
      // If already scrolling, clear it first
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }

      // Set up auto-scrolling interval
      this.autoScrollInterval = setInterval(() => {
        if (this.hasScrollRight) {
          this.scrollCarousel('right');
        } else {
          // Reset to beginning when reaching the end
          this.$refs.carouselTrack.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        }
      }, 5000); // Auto-scroll every 5 seconds
    },

    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    },

    handleScroll() {
      if (!this.$refs.carouselTrack) return;

      const track = this.$refs.carouselTrack;
      this.carouselScrollPosition = track.scrollLeft;

      // Check if we can scroll left or right
      this.hasScrollLeft = track.scrollLeft > 0;
      this.hasScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth - 5); // 5px buffer
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.carouselTrack) {
        this.handleScroll();
        // Start auto-scrolling when component is mounted
        this.startAutoScroll();
      }
    });

    // Add event listeners for mouse interactions
    if (this.$refs.carouselTrack) {
      this.$refs.carouselTrack.addEventListener('mouseenter', this.stopAutoScroll);
      this.$refs.carouselTrack.addEventListener('mouseleave', this.startAutoScroll);
    }
  },
  beforeDestroy() {
    // Clean up interval and event listeners when component is destroyed
    this.stopAutoScroll();
    if (this.$refs.carouselTrack) {
      this.$refs.carouselTrack.removeEventListener('mouseenter', this.stopAutoScroll);
      this.$refs.carouselTrack.removeEventListener('mouseleave', this.startAutoScroll);
    }
  }
}
</script>

<style scoped>
.search-results-carousel {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  margin: 1rem auto;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
}

.theme--dark .search-results-carousel {
  background-color: #1a202c;
}

/* Header Styles - Matching UserUploadsCarousel */
.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.carousel-title-area {
  display: flex;
  align-items: center;
}

.carousel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .carousel-title {
  color: #e2e8f0;
}

.results-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.carousel-controls {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

/* Collapsed View Styles - Matching UserUploadsCarousel */
.collapsed-view {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 6px;
  background-color: #f7fafc;
  cursor: pointer;
}

.theme--dark .collapsed-view {
  background-color: #2d3748;
}

.collapsed-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.collapsed-thumbnails {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.collapsed-thumbnail {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  position: relative;
}

.theme--dark .collapsed-thumbnail {
  border-color: #4a5568;
  background-color: #1a202c;
}

.collapsed-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder-mini {
  width: 100%;
  height: 100%;
  background-color: #edf2f7;
}

.theme--dark .thumbnail-placeholder-mini {
  background-color: #2d3748;
}

.more-indicator {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  color: #4a5568;
  font-size: 0.75rem;
  font-weight: 600;
}

.theme--dark .more-indicator {
  background-color: #4a5568;
  color: #e2e8f0;
}

.collapsed-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.collapsed-text {
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .collapsed-text {
  color: #a0aec0;
}

/* Neural Animation Row - Simplified and Modernized */
.neural-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 0.75rem;
}

.theme--dark .neural-row {
  background-color: #2d3748;
  border-color: #4a5568;
}

/* Carousel Styles - Matching UserUploadsCarousel */
.carousel-container {
  position: relative;
  display: flex;
  margin: 0 0 1rem 0;
  align-items: center;
  padding: 0;
}

.carousel-track {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0.5rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  gap: 16px;
}

.carousel-track::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.carousel-item {
  flex: 0 0 auto;
  width: 180px;
  height: 160px; /* Slightly reduced height to better fit content */
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
}

/* Ensure text is properly visible and not cut off */
.result-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative; /* Added to ensure proper stacking context */
}

.theme--dark .result-card {
  background: #2d3748;
  border-color: #4a5568;
}

.result-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e0;
}

.theme--dark .result-card:hover {
  border-color: #718096;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.result-image {
  height: 50%; /* Reduced from 65% to give more space for text */
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.03);
}

.theme--dark .result-image {
  background: rgba(255, 255, 255, 0.05);
}

.result-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.result-card:hover .result-image img {
  transform: scale(1.05);
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  background-color: #edf2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 0.875rem;
}

.theme--dark .thumbnail-placeholder {
  background-color: #2d3748;
  color: #718096;
}

.result-info {
  padding: 8px 10px;
  height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Add a special class to ensure text doesn't get cut off */
.truncate-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

/* Better handling of title text */
.result-title {
  font-weight: 500;
  font-size: 0.8rem;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 6px;
  color: #2d3748;
  max-height: 2.6em;
  text-overflow: ellipsis;
  word-break: break-word; /* Allow breaking long words if necessary */
}

.theme--dark .result-title {
  color: #e2e8f0;
}

.result-source {
  font-size: 0.7rem;
  color: #718096;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-bottom: 2px; /* Added to ensure the text isn't too close to the bottom */
}

.theme--dark .result-source {
  color: #a0aec0;
}

/* Scroll Arrows - Matching UserUploadsCarousel */
.scroll-arrow {
  position: absolute;
  width: 40px;
  height: 40px;
  background: #f7fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.theme--dark .scroll-arrow {
  background: #2d3748;
  border-color: #4a5568;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.scroll-arrow:hover {
  background: #edf2f7;
  transform: translateY(-1px);
}

.theme--dark .scroll-arrow:hover {
  background: #4a5568;
}

.scroll-arrow.left {
  left: 10px;
}

.scroll-arrow.right {
  right: 10px;
}

.scroll-arrow span {
  color: #4a5568;
  font-size: 0.9rem;
}

.theme--dark .scroll-arrow span {
  color: #e2e8f0;
}

/* External link indicator - More subtle */
.carousel-item::after {
  content: '';
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s ease;
  opacity: 0.7;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%234a5568' viewBox='0 0 16 16'%3E%3Cpath d='M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z'/%3E%3Cpath d='M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 12px;
  border: 1px solid #e2e8f0;
}

.theme--dark .carousel-item::after {
  background-color: rgba(45, 55, 72, 0.7);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23e2e8f0' viewBox='0 0 16 16'%3E%3Cpath d='M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z'/%3E%3Cpath d='M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z'/%3E%3C/svg%3E");
  border-color: #4a5568;
}

.carousel-item:hover::after {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.9);
}

.theme--dark .carousel-item:hover::after {
  background-color: rgba(45, 55, 72, 0.9);
}

/* Highlight for active item - Simplified */
.carousel-item.active {
  box-shadow: 0 0 0 2px #3182ce;
}

.theme--dark .carousel-item.active {
  box-shadow: 0 0 0 2px #63b3ed;
}

.carousel-item.active .result-card {
  border-color: #3182ce;
}

.theme--dark .carousel-item.active .result-card {
  border-color: #63b3ed;
}

/* Status area for the neural animation row */
.status-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Simplified neural animation */
.neural-animation {
  position: relative;
  width: 24px;
  height: 24px;
}

.neural-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #3182ce;
  opacity: 0.3;
  animation: neuralPulse 2s infinite;
}

.neural-ring {
  position: absolute;
  width: 12px;
  height: 12px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #3182ce;
  border-radius: 50%;
  animation: neuralRing 2s infinite;
}

.theme--dark .neural-pulse {
  background: #63b3ed;
}

.theme--dark .neural-ring {
  border-color: #63b3ed;
}

.status-content {
  flex-grow: 1;
}

/* Simplified waveform */
.status-progress {
  height: 24px;
  background: #edf2f7;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

.theme--dark .status-progress {
  background: #2d3748;
}

.waveform {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  height: 100%;
  padding: 0 8px;
}

.waveform-bar {
  flex: 1;
  height: 40%;
  min-width: 3px;
  background: #cbd5e0;
  border-radius: 1px;
  transform-origin: 50% 50%;
  transition: transform 0.2s ease;
  animation: pulse 1.5s ease-in-out infinite;
}

.theme--dark .waveform-bar {
  background: #4a5568;
}

.waveform-bar.active {
  background: #3182ce;
  animation: activePulse 0.8s ease-in-out infinite;
}

.theme--dark .waveform-bar.active {
  background: #63b3ed;
}

/* Results counter - Simplified */
.results-counter {
  font-family: monospace;
  background: #edf2f7;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  color: #4a5568;
}

.theme--dark .results-counter {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.counter-number {
  color: #3182ce;
  font-weight: 600;
}

.theme--dark .counter-number {
  color: #63b3ed;
}

.counter-separator {
  margin: 0 0.25rem;
  opacity: 0.7;
}

.counter-total {
  opacity: 0.7;
}

/* Simplified animations */
@keyframes neuralPulse {
  0%, 100% { transform: scale(0.8); opacity: 0.3; }
  50% { transform: scale(1.2); opacity: 0.1; }
}

@keyframes neuralRing {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: scaleY(0.7); }
  50% { transform: scaleY(0.3); }
}

@keyframes activePulse {
  0%, 100% { transform: scaleY(0.5); }
  50% { transform: scaleY(1); }
}

/* Mobile Responsive Design */
@media screen and (max-width: 768px) {
  .carousel-item {
    width: 160px;
    height: 140px;
  }

  .scroll-arrow {
    width: 32px;
    height: 32px;
  }

  .result-title {
    font-size: 0.75rem;
    margin-bottom: 4px;
    -webkit-line-clamp: 2;
    max-height: 2.4em;
  }

  .result-source {
    font-size: 0.65rem;
  }

  .result-info {
    padding: 6px 8px;
  }

  .collapsed-thumbnail {
    width: 28px;
    height: 28px;
  }

  .more-indicator {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }

  .collapsed-text {
    font-size: 0.8rem;
  }

  .neural-row {
    padding: 0.5rem;
  }

  .status-progress {
    height: 20px;
  }

  .results-counter {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}

</style>
